/**
 * Google-style search suggestions for the FarmPacks header.
 * Mixes Shopify predictive search with curated kit/cover completions.
 */
class FpSearchSuggest extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  #input = null;
  /** @type {HTMLElement | null} */
  #list = null;
  /** @type {number} */
  #active = -1;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #timer = null;
  /** @type {AbortController | null} */
  #fetch = null;
  /** @type {Promise<Record<string, {brands: {brand: string, models: {l: string, c: Record<string, string>}[]}[]}>> | null} */
  #navIndex = null;

  connectedCallback() {
    this.#input = this.querySelector('.fp-header-search__input');
    this.#list = this.querySelector('.fp-search-suggest');
    if (!(this.#input instanceof HTMLInputElement) || !(this.#list instanceof HTMLElement)) return;

    this.#input.addEventListener('input', this.#onInput);
    this.#input.addEventListener('keydown', this.#onKeydown);
    this.#input.addEventListener('focus', this.#onFocus);
    document.addEventListener('pointerdown', this.#onPointerDown);
  }

  disconnectedCallback() {
    this.#input?.removeEventListener('input', this.#onInput);
    this.#input?.removeEventListener('keydown', this.#onKeydown);
    this.#input?.removeEventListener('focus', this.#onFocus);
    document.removeEventListener('pointerdown', this.#onPointerDown);
    this.#close();
  }

  #onInput = () => {
    // Invalidate the old selection immediately, before the debounce expires.
    // Enter and pointer clicks must never use results for the previous text.
    this.#close();
    if ((this.#input?.value.trim().length || 0) < 2) return;
    this.#timer = setTimeout(() => {
      this.#timer = null;
      this.#lookup(this.#input?.value || '');
    }, 160);
  };

  #onFocus = () => {
    const value = this.#input?.value.trim() || '';
    if (value.length >= 2) this.#lookup(value);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (!this.#list || this.#list.hidden) {
      if (event.key === 'Escape') this.#close();
      return;
    }

    const options = this.#options();
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#move(this.#active + 1, options);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.#move(this.#active - 1, options);
    } else if (event.key === 'Enter' && this.#active >= 0 && options[this.#active]) {
      event.preventDefault();
      options[this.#active].click();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.#close();
    }
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    const target = event.target;
    if (target instanceof Node && this.contains(target)) return;
    this.#close();
  };

  /** @param {string} raw */
  async #lookup(raw) {
    const query = raw.trim();
    if (query.length < 2) {
      this.#close();
      return;
    }

    this.#fetch?.abort();
    const abortController = new AbortController();
    this.#fetch = abortController;

    const curated = this.#curated(query);
    const series = this.#seriesRows(query);
    let remote = { queries: [], products: [], collections: [] };

    // Pending rows are not selectable. Input changes have already removed
    // results belonging to the previous query.
    if (this.#list?.hidden) this.#showPending();

    try {
      remote = await this.#fetchRemote(query, abortController.signal);
    } catch (error) {
      if (abortController.signal.aborted) return;
      if (error instanceof DOMException && error.name === 'AbortError') return;
    }

    const seriesRows = await series;
    if (abortController.signal.aborted || this.#fetch !== abortController || this.#input?.value.trim() !== query) return;
    this.#render(query, curated, remote, seriesRows);
  }

  // The menu's brands and models (assets/fp-nav-index.json), fetched the
  // first time a model number is typed.
  #loadNav() {
    if (!this.#navIndex && this.dataset.navIndex) {
      this.#navIndex = fetch(this.dataset.navIndex)
        .then((response) => (response.ok ? response.json() : {}))
        .catch(() => {
          this.#navIndex = null;
          return {};
        });
    }
    return this.#navIndex || Promise.resolve({});
  }

  /*
   * Model numbers ("MF 5713") aren't in the product names, which name the
   * series ("Massey Ferguson 5700 Series"), so Shopify's search missed them.
   * With the make typed, a three- or four-figure number points at that make's
   * series of the same hundreds ("5713" -> "5700 Series", "240" -> "200
   * Series") when there is one. This is a related-series hint, not verified
   * model fitment; it is omitted when predictive search has an exact model.
   * @param {string} query
   */
  async #seriesRows(query) {
    let words = ` ${query.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()} `;
    const numbers = [...words.matchAll(/ (\d{3,4})(?= )/g)].map((match) => match[1]);
    if (!numbers.length) return [];
    words = words.replace(/ mf /g, ' massey ferguson ').replace(/ jd /g, ' john deere ').replace(/ nh /g, ' new holland ');
    const index = await this.#loadNav();
    /** @type {{kind: string, label: string, url: string, extra?: string, image?: string}[]} */
    const rows = [];
    for (const entry of Object.values(index)) {
      for (const brand of entry.brands || []) {
        const name = brand.brand.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
        if (!words.includes(` ${name} `)) continue;
        for (const number of numbers) {
          const series = number.length === 4 ? `${number.slice(0, 2)}00` : `${number[0]}00`;
          const pattern = new RegExp(`(^|[ /])${series}( |/).*series`, 'i');
          for (const model of brand.models) {
            const handle = model.c.high || Object.values(model.c)[0];
            // A group (Harvesters) repeats its types' models: one row each.
            if (!handle || !pattern.test(model.l) || rows.some((row) => row.url === `/products/${handle}`)) continue;
            rows.push({
              kind: 'product',
              label: `${brand.brand} ${model.l}`,
              url: `/products/${handle}`,
              extra: 'Related series — check your model and seat fit',
            });
          }
        }
      }
    }
    return rows.slice(0, 2);
  }

  /**
   * @param {string} query
   * @param {AbortSignal} signal
   */
  async #fetchRemote(query, signal) {
    const base = this.dataset.suggestUrl || '/search/suggest';
    const url = new URL(`${base}.json`, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'query,product,collection');
    url.searchParams.set('resources[limit]', '5');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', 'hide');

    const response = await fetch(url.toString(), {
      signal,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return { queries: [], products: [], collections: [] };

    const payload = await response.json();
    const results = payload?.resources?.results || {};
    return {
      queries: Array.isArray(results.queries) ? results.queries : [],
      products: Array.isArray(results.products) ? results.products : [],
      collections: Array.isArray(results.collections) ? results.collections : [],
    };
  }

  /** @param {string} query */
  #curated(query) {
    const needle = query.toLowerCase();
    const searchUrl = this.dataset.searchUrl || '/search';
    const items = [
      { keys: ['seat', 'cover'], label: 'Tractor seat covers', url: this.dataset.tractorUrl },
      { keys: ['seat', 'cover', 'lorry', 'truck', 'hgv'], label: 'Lorry seat covers', url: this.dataset.lorryUrl },
      { keys: ['seat', 'cover', 'excavator', 'digger'], label: 'Excavator seat covers', url: this.dataset.excavatorUrl },
      { keys: ['seat', 'cover', 'loader', 'dumper'], label: 'Loader seat covers', url: this.dataset.loaderUrl },
      { keys: ['seat', 'cover', 'telehandler', 'loadall', 'manitou', 'merlo'], label: 'Telehandler seat covers', url: this.dataset.telehandlerUrl },
      { keys: ['seat', 'cover', 'forklift', 'access', 'scissor', 'boom', 'linde', 'hyster', 'yale'], label: 'Forklift & access seat covers', url: this.dataset.forkliftUrl },
      { keys: ['seat', 'cover', 'pickup', 'pick-up', '4x4', 'hilux', 'ranger', 'navara', 'defender'], label: 'Pickup & 4x4 seat covers', url: this.dataset.pickupUrl },
      { keys: ['seat', 'cover', 'van', 'transit', 'sprinter', 'vivaro', 'crafter', 'ducato'], label: 'Van seat covers', url: this.dataset.vanUrl },
      { keys: ['brand', 'logo', 'embroider'], label: 'Branded seat covers', url: `${searchUrl}?q=branded+seat+cover&type=product` },
      { keys: ['safety', 'kit', 'emergency', 'pack', 'first aid'], label: 'Farm Emergency Safety Pack', url: this.dataset.kitUrl },
      { keys: ['seat', 'cover'], label: 'seat covers', url: `${searchUrl}?q=seat+covers&type=product` },
      { keys: ['safety', 'kit'], label: 'safety kit', url: `${searchUrl}?q=safety+kit&type=product` },
    ];

    return items.filter((item) => {
      if (!item.url) return false;
      if (item.label.toLowerCase().includes(needle)) return true;
      return item.keys.some((key) => key.startsWith(needle) || needle.startsWith(key));
    });
  }

  /**
   * @param {string} query
   * @param {{label: string, url: string}[]} curated
   * @param {{queries: any[], products: any[], collections: any[]}} remote
   * @param {{kind: string, label: string, url: string, extra?: string, image?: string}[]} [seriesRows]
   */
  #render(query, curated, remote, seriesRows = []) {
    if (!this.#list || !this.#input) return;

    const seen = new Set();
    /** @type {{kind: string, label: string, url: string, extra?: string, image?: string}[]} */
    const rows = [];

    const addQuery = (label, url) => {
      const key = label.toLowerCase();
      if (!label || seen.has(key)) return;
      seen.add(key);
      rows.push({ kind: 'query', label, url });
    };

    const numbers = query.match(/\b\d{3,4}\b/g) || [];
    const exactProducts = remote.products.filter((product) =>
      numbers.some((number) => new RegExp(`\\b${number}\\b`).test(product.title || ''))
    );
    const products = exactProducts.length
      ? [...exactProducts, ...remote.products.filter((product) => !exactProducts.includes(product))]
      : remote.products;
    curated.forEach((item) => addQuery(item.label, item.url));
    remote.queries.forEach((item) => addQuery(item.text, item.url || this.#searchHref(item.text)));
    remote.collections.forEach((item) => addQuery(item.title, item.url));

    products.slice(0, 4).forEach((product) => {
      const image = product.image || product.featured_image?.url || product.featured_image || '';
      rows.push({
        kind: 'product',
        label: product.title,
        url: product.url,
        extra: this.#formatPrice(product),
        image: typeof image === 'string' ? image : '',
      });
    });

    if (!exactProducts.length) {
      seriesRows.forEach((row) => {
        if (rows.some((existing) => existing.url.split('?')[0] === row.url.split('?')[0])) return;
        rows.push(row);
      });
    }

    if (!rows.length) {
      this.#close();
      return;
    }

    this.#list.innerHTML = rows
      .map((row, index) => {
        const label = this.#highlight(row.label, query);
        if (row.kind === 'product') {
          const thumb = row.image
            ? `<img class="fp-search-suggest__thumb" src="${this.#escapeAttr(row.image)}" alt="" width="40" height="40">`
            : `<span class="fp-search-suggest__thumb is-empty" aria-hidden="true"></span>`;
          return `<a class="fp-search-suggest__row fp-search-suggest__row--product" role="option" href="${this.#escapeAttr(row.url)}" data-index="${index}">
            ${thumb}
            <span class="fp-search-suggest__copy"><span class="fp-search-suggest__label">${label}</span>${
              row.extra ? `<span class="fp-search-suggest__meta">${this.#escape(row.extra)}</span>` : ''
            }</span>
          </a>`;
        }
        return `<a class="fp-search-suggest__row" role="option" href="${this.#escapeAttr(row.url)}" data-index="${index}">
          <span class="fp-search-suggest__icon" aria-hidden="true">${this.#searchIcon()}</span>
          <span class="fp-search-suggest__label">${label}</span>
        </a>`;
      })
      .join('');

    this.#active = -1;
    this.#list.hidden = false;
    this.#list.removeAttribute('aria-busy');
    this.#input.setAttribute('aria-expanded', 'true');
  }

  /** @param {string} text */
  #searchHref(text) {
    const searchUrl = this.dataset.searchUrl || '/search';
    return `${searchUrl}?q=${encodeURIComponent(text)}&type=product`;
  }

  /** @param {any} product */
  #formatPrice(product) {
    const price = product.price;
    if (price == null || price === '') return '';
    const amount = Number(price);
    if (Number.isNaN(amount)) return String(price);
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);
    } catch {
      return `£${amount.toFixed(2)}`;
    }
  }

  /**
   * @param {string} text
   * @param {string} query
   */
  #highlight(text, query) {
    const safe = this.#escape(text);
    const match = query.trim();
    if (!match) return safe;
    const index = text.toLowerCase().indexOf(match.toLowerCase());
    if (index < 0) return safe;
    const before = this.#escape(text.slice(0, index));
    const hit = this.#escape(text.slice(index, index + match.length));
    const after = this.#escape(text.slice(index + match.length));
    return `${before}<mark>${hit}</mark>${after}`;
  }

  #options() {
    return Array.from(this.#list?.querySelectorAll('.fp-search-suggest__row') || []);
  }

  /**
   * @param {number} next
   * @param {Element[]} options
   */
  #move(next, options) {
    if (!options.length) return;
    const index = (next + options.length) % options.length;
    options.forEach((option, i) => option.classList.toggle('is-active', i === index));
    this.#active = index;
    options[index]?.scrollIntoView({ block: 'nearest' });
  }

  /** Placeholder rows for the gap between keystroke and response. */
  #showPending() {
    if (!this.#list || !this.#input) return;
    this.#list.innerHTML = Array.from({ length: 4 })
      .map(
        () =>
          '<div class="fp-search-suggest__row fp-search-suggest__row--skel" aria-hidden="true">' +
          '<span class="fp-skel fp-skel-row__icon"></span>' +
          '<span class="fp-skel fp-skel-row__label" style="width:' +
          (52 + Math.round(Math.random() * 28)) +
          '%"></span></div>'
      )
      .join('');
    this.#active = -1;
    this.#list.hidden = false;
    // Busy, not populated: screen readers should not announce an option count
    // for rows that are only placeholders.
    this.#list.setAttribute('aria-busy', 'true');
    this.#input.setAttribute('aria-expanded', 'true');
  }

  #close() {
    // Closing, clearing and disconnecting cancel both pending stages. A late
    // response must not reopen a dismissed dropdown, even for the same query.
    if (this.#timer) clearTimeout(this.#timer);
    this.#timer = null;
    this.#fetch?.abort();
    this.#fetch = null;
    if (this.#list) {
      this.#list.hidden = true;
      this.#list.innerHTML = '';
      this.#list.removeAttribute('aria-busy');
    }
    this.#input?.setAttribute('aria-expanded', 'false');
    this.#active = -1;
  }

  #searchIcon() {
    return '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 20 20" fill="none"><circle cx="8.5" cy="8.5" r="5.25" stroke="currentColor" stroke-width="1.5"/><path d="m12.5 12.5 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
  }

  /** @param {string} value */
  #escape(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /** @param {string} value */
  #escapeAttr(value) {
    return this.#escape(value).replace(/'/g, '&#39;');
  }
}

customElements.define('fp-search-suggest', FpSearchSuggest);
